import { useState } from 'react';
import { X, Loader2, Upload, Image as ImageIcon } from 'lucide-react';
import api from '../api/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  filter: 'Filtro', oil: 'Aceite/Lubricante', belt: 'Correa/Banda',
  bearing: 'Rodamiento', tire: 'Neumático', electrical: 'Eléctrico',
  hydraulic: 'Hidráulico', other: 'Otros',
};

export default function InventoryItemModal({ item, areaId, folderId, onClose, onSaved }) {
  const isEdit = !!item;
  const [form, setForm] = useState({
    code: item?.code || '',
    name: item?.name || '',
    category: item?.category || 'other',
    quantity: item?.quantity ?? 0,
    min_quantity: item?.min_quantity ?? 1,
    unit: item?.unit || 'unidad',
    location: item?.location || '',
    supplier: item?.supplier || '',
    unit_price: item?.unit_price ?? '',
    notes: item?.notes || '',
    photo_url: item?.photo_url || '',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setError('');
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, photo_url: res.data.url }));
    } catch (err) {
      setError(err.response?.data?.detail || 'No se pudo subir la foto');
    } finally { setUploading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    const payload = {
      ...form,
      quantity: parseFloat(form.quantity) || 0,
      min_quantity: parseFloat(form.min_quantity) || 0,
      unit_price: form.unit_price === '' ? null : parseFloat(form.unit_price),
      area_id: areaId,
      folder_id: folderId,
    };
    try {
      if (isEdit) await api.put(`/inventory/${item.id}`, payload);
      else await api.post('/inventory', payload);
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const field = (label, key, type = 'text', props = {}) => (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      <input
        type={type}
        value={form[key]}
        onChange={e => setForm({ ...form, [key]: e.target.value })}
        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none"
        data-testid={`item-${key}`}
        {...props}
      />
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto" data-testid="item-modal">
        <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white">
          <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>
            {isEdit ? 'Editar Repuesto' : 'Nuevo Repuesto'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1" data-testid="close-item-modal"><X size={18} /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm" data-testid="item-modal-error">{error}</div>}

          <div className="grid grid-cols-2 gap-4">
            {field('Código', 'code', 'text', { required: true, placeholder: 'Ej: FIL-AC-001' })}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Categoría</label>
              <select
                value={form.category}
                onChange={e => setForm({ ...form, category: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none bg-white"
                data-testid="item-category"
              >
                {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>

          {field('Artículo', 'name', 'text', { required: true, placeholder: 'Denominación del repuesto' })}

          <div className="grid grid-cols-3 gap-4">
            {field('Stock actual', 'quantity', 'number', { step: '0.01', min: '0' })}
            {field('Mínimo', 'min_quantity', 'number', { step: '0.01', min: '0' })}
            {field('Unidad', 'unit', 'text', { placeholder: 'unidad' })}
          </div>

          <div className="grid grid-cols-3 gap-4">
            {field('Ubicación / estantería', 'location', 'text', { placeholder: 'Ej: Estante A-3' })}
            {field('Proveedor', 'supplier')}
            {field('Precio unit. (€)', 'unit_price', 'number', { step: '0.01', min: '0' })}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Observaciones</label>
            <textarea
              value={form.notes} rows={2}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none resize-none"
              data-testid="item-notes"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Foto del repuesto</label>
            <div className="flex items-center gap-3">
              <div className="w-16 h-16 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden flex-shrink-0">
                {form.photo_url
                  ? <img src={`${API_URL}${form.photo_url}`} alt="Repuesto" className="w-full h-full object-cover" data-testid="item-photo-preview" />
                  : <ImageIcon size={20} className="text-gray-300" />}
              </div>
              <label className="flex items-center gap-2 px-3 py-2 text-xs font-medium border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {uploading ? 'Subiendo...' : form.photo_url ? 'Cambiar foto' : 'Subir foto'}
                <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} data-testid="item-photo-input" />
              </label>
              {form.photo_url && (
                <button type="button" onClick={() => setForm({ ...form, photo_url: '' })}
                  className="text-xs text-red-600 hover:underline" data-testid="remove-item-photo">Quitar</button>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50" data-testid="cancel-item-btn">Cancelar</button>
            <button type="submit" disabled={saving || uploading}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-[#16a34a] text-white rounded-lg hover:bg-[#15803d] disabled:opacity-50"
              data-testid="save-item-btn">
              {saving && <Loader2 size={14} className="animate-spin" />}
              {saving ? 'Guardando...' : isEdit ? 'Guardar Cambios' : 'Crear Repuesto'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
